/**
 * Hook de datos de la lista de Productos. PAGINACIÓN / FILTRO / ORDEN SERVER-SIDE
 * (vía `productsApi`), con fallback OFFLINE sobre el caché compartido de IndexedDB
 * (que POS/Menú mantienen frescos).
 *
 * Antes la página traía el catálogo entero (`.limit(10000)`) y filtraba/paginaba en
 * memoria → truncaba en silencio al cruzar las 1000 filas de PostgREST. Ahora se pide
 * solo la ventana visible y los conteos salen de queries `count: 'exact'`.
 *
 * Es param-driven: el estado de búsqueda/filtros/orden/pestaña lo dueña la página (lo
 * manejan useListViews/useStandardListFilters) y se pasa acá como parámetros.
 */
import { useCallback, useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useDebounce } from '@smarty/shared';
import { devLog } from '@/lib/utils';
import { getErrorMessage } from '@/lib/getErrorMessage';
import {
  fetchProductsPage,
  fetchInventoryTotals,
  fetchMarketplaceStates,
  filterSortProducts,
  type ProductMarketplaceState,
  type ProductRow,
  type ProductSortKey,
  type ProductStatusView,
} from '@/lib/productsApi';

export interface UseProductsDataParams {
  tenantId: string | null;
  authLoading: boolean;
  offset: number;
  pageSize: number;
  /** Búsqueda cruda; el hook la debouncea. */
  search: string;
  statusView: ProductStatusView;
  statusFilter: string[];
  categories: string[];
  sortBy: ProductSortKey;
  sortDir: 'asc' | 'desc';
  onError: (message: string) => void;
}

export interface UseProductsDataReturn {
  /** SOLO la página visible (ya filtrada/ordenada por el servidor). */
  products: ProductRow[];
  /** Total REAL que matchea el filtro (count del servidor). */
  total: number;
  /** Total de inventario por producto, SOLO para la página visible. */
  inventoryTotalByProductId: Record<string, number>;
  /** Estado en marketplaces por producto, SOLO para la página visible. Vacío si no se pudo leer. */
  marketplaceByProductId: Record<string, ProductMarketplaceState>;
  loading: boolean;
  reload: () => Promise<void>;
}

/** Lo que trae una página completa de la lista: los productos y sus dos anexos. */
interface PaginaProductos {
  items: ProductRow[];
  total: number;
  inventory: Record<string, number>;
  marketplaces: Record<string, ProductMarketplaceState>;
}

/**
 * La consulta de la lista de productos, como OPCIONES reutilizables.
 *
 * ⭐ Existe para que la RUTA y la PANTALLA compartan exactamente la misma definición (ADR-0012,
 * paso 2). El `loader` de la ruta arranca la carga cuando arranca la navegación —antes de que el
 * componente exista— y la pantalla, al montar, encuentra el dato ya puesto.
 *
 * 🪤 Si la clave o el `queryFn` se escribieran dos veces, el `loader` calentaría una entrada que la
 * pantalla nunca lee: el usuario paga la consulta dos veces y no gana nada. Ya pasó con la precarga
 * del Inicio. Una sola definición, importada por los dos.
 */
export function opcionesProductos(p: {
  tenantId: string | null;
  offset: number;
  pageSize: number;
  search: string;
  statusView: ProductStatusView;
  statusFilter: string[];
  categories: string[];
  sortBy: ProductSortKey;
  sortDir: 'asc' | 'desc';
}) {
  const { tenantId, offset, pageSize, search, statusView, statusFilter, categories, sortBy, sortDir } = p;
  const debouncedSearch = search;
  return {
    queryKey: ['products', 'page', tenantId, offset, pageSize, debouncedSearch, statusView, statusFilter, categories, sortBy, sortDir],
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<PaginaProductos> => {
        // Offline: leer el catálogo cacheado (POS/Menú lo pueblan) y paginar en memoria.
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          const { getCachedProducts } = await import('@/lib/db');
          const cached = await getCachedProducts<ProductRow>();
          const filtered = filterSortProducts(cached, {
            search: debouncedSearch, statusView, statusFilter, categories, sortBy, sortDir,
          });
          return {
            items: filtered.slice(offset, offset + pageSize),
            total: filtered.length,
            inventory: {},
            marketplaces: {},
          };
        }

        const page = await fetchProductsPage({
          tenantId: tenantId!, offset, limit: pageSize, search: debouncedSearch,
          statusView, statusFilter, categories, sortBy, sortDir,
        });
        const ids = page.items.map((p) => p.id);

        // ⭐ Inventario y marketplaces van EN PARALELO. Los dos sólo necesitan los ids de la página,
        // así que encadenarlos era un viaje al servidor de más esperando por nada.
        //
        // Medido con el RUM del panel (ADR-0012, paso 5): Productos era la pantalla más lenta del
        // panel —p75 de 1.530 ms— pese a estar ya convertida a la caché. Ese era el punto: la caché
        // no arregla un waterfall que ocurre DENTRO de la consulta. Tres viajes en fila
        // (productos → inventario → marketplaces) pasan a dos (productos → los otros dos juntos).
        //
        // 🪤 Los marketplaces alimentan SOLO el desplegable de Canales. Si fallan, la lista sigue
        // funcionando: se muestra lo que sí se sabe (Tienda y POS) en vez de dejar al vendedor sin
        // catálogo por no poder leer Mercado Libre. Por eso van con `allSettled` y no con `all`:
        // `all` haría que un fallo de Mercado Libre tumbe también el inventario.
        const [invRes, mkRes] = await Promise.allSettled([
          fetchInventoryTotals(tenantId!, ids),
          fetchMarketplaceStates(tenantId!, ids),
        ]);

        if (invRes.status === 'rejected') throw invRes.reason;
        if (mkRes.status === 'rejected') {
          devLog.error('No se pudo leer el estado en marketplaces:', mkRes.reason);
        }

        const marketplaces: Record<string, ProductMarketplaceState> =
          mkRes.status === 'fulfilled' ? mkRes.value : {};

        return { items: page.items, total: page.total, inventory: invRes.value, marketplaces };
      },
  };
}

export function useProductsData(params: UseProductsDataParams): UseProductsDataReturn {
  const { tenantId, authLoading, offset, pageSize, statusView, statusFilter, categories, sortBy, sortDir, onError } = params;

  // Debounce: sin esto cada tecla dispara una query al servidor.
  const debouncedSearch = useDebounce(params.search, 300);

  /**
   * 🚨 Antes esto era `useEffect` + cinco `useState`. Con ese patrón, volver a Productos SIEMPRE
   * recargaba: no hay dónde guardar la respuesta entre desmontajes. Ver ADR-0012 §Apéndice.
   *
   * 🪤 Los tres pedidos van en UNA sola query y no en tres, a propósito: inventario y marketplaces
   * necesitan los IDs de la página, o sea que dependen de la primera respuesta. Partirlos en queries
   * separadas obligaría a encadenarlas con `enabled`, y la lista quedaría mostrando productos sin
   * stock durante un instante — un parpadeo de datos incompletos peor que esperar.
   *
   * ⭐ `keepPreviousData`: al paginar, buscar o filtrar, la tabla anterior SIGUE en pantalla mientras
   * llega la nueva, en vez de vaciarse y mostrar un spinner.
   */
  const query = useQuery<PaginaProductos>({
    ...opcionesProductos({
      tenantId, offset, pageSize, search: debouncedSearch,
      statusView, statusFilter, categories, sortBy, sortDir,
    }),
    enabled: !authLoading && !!tenantId,
  });

  // 🪤 TanStack Query v5 quitó `onError` de `useQuery`: el error se observa. Avisar desde el render
  // sería un efecto secundario en medio del render.
  useEffect(() => {
    if (!query.error) return;
    devLog.error('Error cargando productos:', query.error);
    onError(getErrorMessage(query.error));
  }, [query.error, onError]);

  const reload = useCallback(async () => { await query.refetch(); }, [query]);

  return {
    products: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    inventoryTotalByProductId: query.data?.inventory ?? {},
    marketplaceByProductId: query.data?.marketplaces ?? {},
    // 🪤 `isPending` («no tengo nada que mostrar»), NO `isFetching` («estoy revalidando»): con
    // `isFetching` el spinner taparía datos cacheados que ya están en pantalla.
    loading: query.isPending,
    reload,
  };
}
